import { Clipboard } from '@angular/cdk/clipboard';
import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { GlobalService } from 'app/global/global.service';
import { DocumentsService } from 'app/main/pages/documents/documents.service';
import { EditroleModalComponent } from 'app/main/pages/general-modals/editrole-modal/editrole-modal.component';
import { environment } from 'environments/environment';
import { SigningStatusEnum } from '../../enums/enum';
import { SendReminderComponent } from '../send-reminder/send-reminder.component';
import { SigningSidebarService } from '../signing-sidebar.service';
import { Participants, RecipientRole } from './participants.dto';
import { ParticipantsService } from './participants.service';
import { ResendEmailModalComponent } from 'app/main/pages/modals/general-modals/resend-email-modal/resend-email-modal.component';
import { DocumentIndexDataService } from 'app/main/pages/documents/document-index-data-modal/document-index-data.service';
import { of, Subject, take, takeUntil } from 'rxjs';
import { PermissionsService } from 'app/global/permissions/permissions.service';
import { PermissionsEnum } from 'app/global/permissions/permissions.enum';
import { Field } from 'app/interfaces/fields';
import { ParseNamePipe } from 'app/pipes/parse-name.pipe';
import { updateOrganizationField, updatePersonField, updateSignatureBox } from 'app/global/synchronize';
import { RecipientModalComponent } from 'app/main/pages/general-modals/recipient-modal/recipient-modal.component';
import { RoleDetail } from '../../signing-page/signing-page.dto';
import { setRecipientData } from 'app/global/get-recipient-data';
import {
  ChartComponent,
  ApexAxisChartSeries,
  ApexChart,
  ApexXAxis,
  ApexDataLabels,
  ApexYAxis,
  ApexLegend,
  ApexFill,
} from "ng-apexcharts";
import { DocumentListingFacade } from 'app/main/pages/documents/store/document-listing.facade';
import { DocumentHelperFacade } from 'app/main/pages/documents/document-right-sidebar/document-helper.facade';

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  xaxis: ApexXAxis;
  dataLabels: ApexDataLabels;
  yaxis: ApexYAxis;
  colors: string[];
  legend: ApexLegend;
  fill: ApexFill;
  stroke: {
    width: 1;
  };
};

@Component({
  selector: 'app-participants',
  templateUrl: './participants.component.html',
  styleUrl: './participants.component.scss'
})
export class ParticipantsComponent implements OnInit, OnDestroy {


  @ViewChild("chart") chart: ChartComponent;
  public chartOptions: Partial<ChartOptions>;
  @Input() roleId: number;
  @Input() document_id: number;
  @Input() is_sender: number;
  @Input() view: string = 'signing_area';
  @Input() roleDetail: RoleDetail;
  private destroy$ = new Subject<void>();
  public canEditRecipient$ = of(false);
  public canSendReminder$ = of(false);
  private constFirstName = 'First name';
  private constLastName = 'Last name';
  public enableDocumentAnalyticsFeature: boolean = false;
  public selectedDocument$ = this._documentsFacade.selectedDocument$
  /**
   * @param singingPage 
   * @param globalService 
   * @param copy 
   * @param modal 
   */
  constructor(
    private singingPage: SigningSidebarService,
    private globalService: GlobalService,
    private copy: Clipboard,
    private modal: NgbModal,
    private documentService: DocumentsService,
    private participantsService: ParticipantsService,
    private _documentIndexDataService$: DocumentIndexDataService,
    private _permissionService: PermissionsService,
    private parseName: ParseNamePipe,
    private _documentsFacade: DocumentListingFacade,
    public _documentHelperFacade: DocumentHelperFacade,
  ) {
    this.chartOptions = {

      series: [
        {
          name: "Recipient 1",
          data: this.generateDayWiseTimeSeries(
            new Date("11 Feb 2017 GMT").getTime(),
            20,
            {
              min: 10,
              max: 60
            }
          )
        },
        {
          name: "Recipient 2",
          data: this.generateDayWiseTimeSeries(
            new Date("11 Feb 2017 GMT").getTime(),
            20,
            {
              min: 10,
              max: 20
            }
          )
        },
        {
          name: "Recipient 3",
          data: this.generateDayWiseTimeSeries(
            new Date("11 Feb 2017 GMT").getTime(),
            20,
            {
              min: 10,
              max: 15
            }
          )
        }
      ],
      stroke: {
        width: 1,
      },
      chart: {
        type: "area",
        height: 350,
        stacked: true,
        toolbar: {
          show: false
        },
      },

      colors: ["#008FFB", "#00E396", "#CED4DC"],
      dataLabels: {
        enabled: false
      },
      fill: {
        type: "gradient",
        gradient: {
          opacityFrom: 0.6,
          opacityTo: 0.8
        }
      },
      legend: {
        position: "top",
        horizontalAlign: "left"
      },
      xaxis: {
        type: "datetime",
        labels: {
          show: true,
          style: {
            fontSize: '12px',
            fontFamily: 'Montserrat", Helvetica, Arial, serif',
            fontWeight: 400,
          },
        }
      },
      yaxis: {
        labels: {
          show: true,
          style: {
            fontSize: '12px',
            fontFamily: 'Montserrat", Helvetica, Arial, serif',
            fontWeight: 400,
          },
        }
      },
    };
  }

  public generateDayWiseTimeSeries = function (baseval, count, yrange) {
    let i = 0;
    const series = [];
    while (i < count) {
      const x = baseval;
      const y =
        Math.floor(Math.random() * (yrange.max - yrange.min + 1)) + yrange.min;

      series.push([x, y]);
      baseval += 86400000;
      i++;
    }
    return series;
  };

  public participientLoader: boolean = false;
  public documentParticipants: Participants;
  public signingStatusEnum = SigningStatusEnum;
  ngOnInit(): void {
    this.decideParticipantsRequest();

    this._documentIndexDataService$.shouldRefreshParticipants$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.fetchParticipants();
    });

    if (this.is_sender) {
      this.canEditRecipient$ = this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_edit_recipient_information_on_signing_page);
      this.canSendReminder$ = this._permissionService.isNotRestricted(PermissionsEnum.restrict_access_send_reminders_manually);
    }
  }




  decideParticipantsRequest() {
    this.participientLoader = true;
    if (this.view === 'documents') {
      this.fetchParticipants();
    } else {
      this.getTheParticipants();
    }
  }




  // For Documents Area
  fetchParticipants() {
    this.documentService.getDocumentsParticipants(this.document_id).subscribe({
      next: (participants: Participants) => this.collectParticipantsData(participants, false),
      error: (err: string) => this.globalService.toastrError(err)
    });
  }





  // For Signing area
  getTheParticipants() {
    this.singingPage.getParticipants(this.roleId, this.document_id).subscribe({
      next: (participants: Participants) => this.collectParticipantsData(participants, true),
      error: (err: string) => {
        this.globalService.toastrError(err);
        this.participientLoader = false;
      },
    })
  }



  /**
   * Collect participants data
   * @param participants 
   */
  private collectParticipantsData(participants: Participants, updatePerson: boolean) {
    this.documentParticipants = participants;
    if (updatePerson) {
      this.documentParticipants.recipient_roles.map((role) => {
        if (role.id === Number(this.roleId)) {
          role.personfields.map((field) => {
            const fieldData: Field = this.globalService.findField(this.globalService.fields_data, 'selected_field_type_id', field.id, "person_field");
            if (fieldData) {
              if (field.name === this.constFirstName) {
                const recipient = this.parseName.transform(fieldData.value);
                field.value = recipient.firstname;
                role.personfields[1].value = recipient.lastname;
              } else {
                field.value = fieldData.value;
              }
            }
          });
          role.orgfields.map((field) => {
            const fieldData: Field = this.globalService.findField(this.globalService.fields_data, 'selected_field_type_id', field.id, "org_field");
            if (fieldData) {
              field.value = fieldData.value;
            }
          })
        }
      })
    }
    this.participientLoader = false;
  }

  editPerson(participant: RecipientRole, index: number) {
    const recipientRef: NgbModalRef = this.modal.open(RecipientModalComponent, {
      backdrop: 'static',
      windowClass: 'edit-recipient-modal'
    });
    recipientRef.componentInstance._recipientRole = participant;
    recipientRef.componentInstance._updateRecipient = true;
    recipientRef.componentInstance._navigation = 'recipient';
    recipientRef.componentInstance._activeIndex = 0;
    recipientRef.componentInstance.isEditParticipientFromSigningPage = true;
    recipientRef.result.then((result) => {
      if (result) {
        const cloneResult = { ...result };
        this.roleDetail.recipient = setRecipientData(cloneResult.personField);
        this.roleDetail.personfields = cloneResult.personfields;
        updateSignatureBox(participant.id, this.globalService, cloneResult.recipient, participant?.role_name);
        for (const field of cloneResult.personField) {
          if (field.name === this.constLastName) continue;
          const fieldData: Field = this.globalService.findField(this.globalService.fields_data, 'selected_field_type_id', field.id, "person_field");
          if (fieldData && !field?.lock) {
            updatePersonField({ ...field }, cloneResult.recipient?.id, participant.id, this.globalService, 'person_field', cloneResult.recipient);
          } else {
            if (field.is_enable && !field?.lock) {
              let value = field.value;
              if (field.name === this.constFirstName) {
                value = `${cloneResult?.recipient?.firstname} ${cloneResult?.recipient?.lastname ?? ''}`
              }
              this.globalService.fields_data[field.id] = { selected_field_role_id: participant.id, selected_field_type_id: field.id, selected_for_role_id: participant.id, update_recipient: true, value: value, field_info: 'recipient_field' }
            }
          }
        }
        for (const orgfield of cloneResult.orgfields) {
          const fieldData: Field = this.globalService.findField(this.globalService.fields_data, 'selected_field_type_id', orgfield.id, "org_field");
          if (fieldData && !orgfield?.lock) {
            updateOrganizationField(orgfield, participant.recipient_id, participant.id, this.globalService);
          } else {
            if (orgfield.is_enable && !orgfield?.lock) {
              this.globalService.fields_data[orgfield.id] = { selected_field_role_id: participant.id, selected_field_type_id: orgfield.id, selected_for_role_id: participant.id, update_recipient: true, value: orgfield.value, field_info: 'recipient_field' }
            }
          }
        }

        this.documentParticipants.recipient_roles[index] = { ...this.documentParticipants.recipient_roles[index], ...{ personfields: cloneResult.personfields, orgfields: cloneResult.orgfields, recipient: cloneResult.recipient } };
      }
    });
  }


  /**
   * make a phone call to sender
   * @param phoneno 
   */
  makePhoneCall(phoneno: string) {
    window.open('tel:' + phoneno, '_self');
  }

  /**
   * send a email to sender
   * @param email 
   */
  openEmail(email: string) {
    window.open('mailto:' + email, '_self');
  }

  /**
   * copy the document link
   * @param party 
   */
  async copyDocumentLink(party: RecipientRole) {
    await this.copy.copy(`${environment.appUrl}s/${party.id}/${party.recipient_role_detail.unique_id}`);
  }

  /**
   * sender reminder to the recipient
   * @param party 
   */
  sendReminder(party: RecipientRole) {
    const sendReminder: NgbModalRef = this.modal.open(SendReminderComponent, {
      size: 'sm',
      backdrop: 'static'
    });
    sendReminder.componentInstance.party = party;
  }

  /**
   * edit participant
   * @param party 
   */
  editParticipant(party: RecipientRole) {
    const roleRef: NgbModalRef = this.modal.open(EditroleModalComponent, {
      size: 'lg',
      windowClass: 'edit-role-modal',
      backdrop: 'static'
    });
    roleRef.componentInstance.role = party;
    roleRef.componentInstance.orderLength = this.documentParticipants.recipient_roles.length - 1;
    roleRef.componentInstance.document_id = party.roleable_id;
    roleRef.componentInstance.isEditParticipient = true;
    roleRef.componentInstance.setForcePhoneNumberValidation = true;
    roleRef.result.then((data) => {
      if (data?.success !== undefined && data?.success) {
        this.decideParticipantsRequest();
      }
    });
  }



  /**
   * Allows the recipient to forward the document to another party.
   * This function handles the logic to determine if the forwarding option
   * should be enabled for a particular role based on certain conditions
   * triggered by the event.
   *
   * @param {Event} $event - The event object that triggered this function.
   * @param {Object} index - The index of the recipient which includes details
   *                        necessary to decide if forwarding is permitted.
   */
  allowToForward($event, index) {
    $event.preventDefault();
    this.togglePermission($event, index, 'allow_to_forward');
  }

  /**
     * Allows the recipient to invite to read the document to another party.
     * This function handles the logic to determine if the invite to read option
     * should be enabled for a particular role based on certain conditions
     * triggered by the event.
     *
     * @param {Event} $event - The event object that triggered this function.
     * @param {Object} index - The index of the recipient which includes details
     *                        necessary to decide if forwarding is permitted.
     */
  allowToRead($event, index) {
    $event.preventDefault();
    this.togglePermission($event, index, 'allow_to_read');
  }


  /**
   * Toggles the "allow to invite" status for a participant at the specified index.
   * Prevents the default event behavior, updates the local participant's "allow_to_invite" property,
   * and calls the participants service to persist the change.
   *
   * @param $event - The event object triggered by the user interaction.
   * @param index - The index of the participant in the recipient_roles array.
   */
  allowToInvite($event, index) {
    $event.preventDefault();
    this.togglePermission($event, index, 'allow_to_invite');
  }


  /**
   * Generic function to toggle permissions (forward, read, sign) for a participant.
   *
   * @param $event - The event triggered by user interaction.
   * @param index - The index of the participant in the recipient_roles array.
   * @param permissionKey - The permission field to toggle (e.g., 'allow_to_forward', 'allow_to_read', 'allow_to_invite').
   */
  togglePermission($event: Event, index: number, permissionKey: 'allow_to_forward' | 'allow_to_read' | 'allow_to_invite') {
    $event.preventDefault();

    const participant = this.documentParticipants.recipient_roles[index];
    const currentValue = participant[permissionKey];

    // Optimistically update UI
    participant[permissionKey] = !currentValue;

    this.participantsService.allowToAccess(participant.id, permissionKey, !currentValue).subscribe({
      error: (error) => {
        // rollback on error
        participant[permissionKey] = currentValue;
        this.globalService.toastrError(error.message);
      }
    });
  }


  openResendEmailModal(recipientRoleId: number) {
    const modalRef$ = this.modal.open(ResendEmailModalComponent);
    modalRef$.componentInstance.recipientRoleId = recipientRoleId;
    modalRef$.componentInstance.documentId = this.document_id;
    modalRef$.result.then((result: { refreshParticipants: number, template: Participants }) => {
      if (result?.refreshParticipants == 1) {
        this.decideParticipantsRequest();
      }
    })
  }

  activeDeactiveReminder(index: number, status: boolean){
    this.documentParticipants.recipient_roles[index].is_reminder = status;
    this.participantsService.activeDeactiveReminder(this.documentParticipants.recipient_roles[index].id, status).subscribe();
  }

  openFullScreen() {
    if (this.view === 'documents') {
      this.selectedDocument$.pipe(take(1)).subscribe(selectedDocument => {
        this._documentHelperFacade.redirectToFullScreen(selectedDocument);
      })
    }
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
